import { create } from 'zustand';
import Dexie, { type Table } from 'dexie';
import { v4 as uuid } from 'uuid';
import type { Campaign, Creative, Asset, CreativeContent } from '@/types/creative';

// IndexedDB Schema
class AdStudioDB extends Dexie {
  campaigns!: Table<Campaign>;
  creatives!: Table<Creative>;
  assets!: Table<Asset>;

  constructor() {
    super('AdStudioDB');

    // Version 1: Original schema with 'projects' table
    this.version(1).stores({
      campaigns: 'id, name, createdAt, updatedAt',
      projects: 'id, campaignId, name, formatId, createdAt, updatedAt',
      assets: 'id, campaignId, name, type, createdAt',
    });

    // Version 2: Rename projects -> creatives, formatId -> primaryFormatId
    this.version(2).stores({
      campaigns: 'id, name, createdAt, updatedAt',
      creatives: 'id, campaignId, name, primaryFormatId, createdAt, updatedAt',
      assets: 'id, campaignId, name, type, createdAt',
    }).upgrade(async tx => {
      // Migrate projects to creatives
      const oldProjects = await (tx as any).table('projects').toArray();
      const creatives = oldProjects.map((p: any) => ({
        ...p,
        primaryFormatId: p.formatId,
        templateId: p.stylePreset,
      }));

      // Add to creatives table
      if (creatives.length > 0) {
        await tx.table('creatives').bulkAdd(creatives);
      }

      // Update campaigns: projectIds -> creativeIds
      const campaigns = await tx.table('campaigns').toArray();
      for (const campaign of campaigns) {
        if ((campaign as any).projectIds) {
          await tx.table('campaigns').update(campaign.id, {
            creativeIds: (campaign as any).projectIds,
          });
        }
      }
    });
  }
}

const db = new AdStudioDB();

// LocalStorage keys for persisting last selection
const LAST_CAMPAIGN_KEY = 'adstudio:lastCampaignId';
const LAST_CREATIVE_KEY = 'adstudio:lastCreativeId';

interface CreativeState {
  // Current selections
  currentCampaignId: string | null;
  currentCreativeId: string | null;

  // Data (cached from IndexedDB)
  campaigns: Campaign[];
  creatives: Creative[];
  assets: Asset[];

  // Loading states
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

interface CreativeActions {
  // Initialize / Load
  loadAll: () => Promise<void>;

  // Campaign CRUD
  createCampaign: (name: string, description?: string) => Promise<Campaign>;
  updateCampaign: (id: string, updates: Partial<Campaign>) => Promise<void>;
  deleteCampaign: (id: string) => Promise<void>;
  selectCampaign: (id: string | null) => void;

  // Creative CRUD
  createCreative: (
    campaignId: string,
    name: string,
    primaryFormatId: string,
    templateId: string,
    content: CreativeContent
  ) => Promise<Creative>;
  updateCreative: (id: string, updates: Partial<Creative>) => Promise<void>;
  deleteCreative: (id: string) => Promise<void>;
  selectCreative: (id: string | null) => void;
  duplicateCreative: (id: string) => Promise<Creative | null>;

  // Asset CRUD
  uploadAsset: (
    file: File,
    campaignId?: string
  ) => Promise<Asset>;
  deleteAsset: (id: string) => Promise<void>;

  // Export/Import
  exportCreative: (id: string) => Promise<Blob>;
  importCreative: (file: File, campaignId: string) => Promise<Creative | null>;
}

export const useCreativeStore = create<CreativeState & CreativeActions>((set, get) => ({
  currentCampaignId: null,
  currentCreativeId: null,
  campaigns: [],
  creatives: [],
  assets: [],
  isLoading: false,
  isInitialized: false,
  error: null,

  loadAll: async () => {
    set({ isLoading: true, error: null });
    try {
      const [campaigns, creatives, assets] = await Promise.all([
        db.campaigns.toArray(),
        db.creatives.toArray(),
        db.assets.toArray(),
      ]);

      // Restore last selection if valid
      let currentCampaignId: string | null = null;
      let currentCreativeId: string | null = null;

      const lastCampaignId = localStorage.getItem(LAST_CAMPAIGN_KEY);
      const lastCreativeId = localStorage.getItem(LAST_CREATIVE_KEY);

      if (lastCampaignId && campaigns.some(c => c.id === lastCampaignId)) {
        currentCampaignId = lastCampaignId;

        if (lastCreativeId && creatives.some(c => c.id === lastCreativeId && c.campaignId === lastCampaignId)) {
          currentCreativeId = lastCreativeId;
        }
      }

      set({
        campaigns,
        creatives,
        currentCampaignId,
        currentCreativeId,
        assets,
        isLoading: false,
        isInitialized: true,
      });
    } catch (e) {
      set({ error: (e as Error).message, isLoading: false, isInitialized: true });
    }
  },

  createCampaign: async (name, description) => {
    const campaign: Campaign = {
      id: uuid(),
      name,
      description,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      creativeIds: [],
    };
    await db.campaigns.add(campaign);
    set((s) => ({ campaigns: [...s.campaigns, campaign] }));
    return campaign;
  },

  updateCampaign: async (id, updates) => {
    const updatedData = { ...updates, updatedAt: new Date().toISOString() };
    await db.campaigns.update(id, updatedData);
    set((s) => ({
      campaigns: s.campaigns.map((c) =>
        c.id === id ? { ...c, ...updatedData } : c
      ),
    }));
  },

  deleteCampaign: async (id) => {
    // Delete all creatives in campaign
    const creativeIds = get().creatives
      .filter((c) => c.campaignId === id)
      .map((c) => c.id);
    await db.creatives.bulkDelete(creativeIds);
    await db.campaigns.delete(id);
    set((s) => ({
      campaigns: s.campaigns.filter((c) => c.id !== id),
      creatives: s.creatives.filter((c) => c.campaignId !== id),
      currentCampaignId: s.currentCampaignId === id ? null : s.currentCampaignId,
    }));
  },

  selectCampaign: (id) => {
    if (id) {
      localStorage.setItem(LAST_CAMPAIGN_KEY, id);
    } else {
      localStorage.removeItem(LAST_CAMPAIGN_KEY);
      localStorage.removeItem(LAST_CREATIVE_KEY);
    }
    set({ currentCampaignId: id, currentCreativeId: null });
  },

  createCreative: async (campaignId, name, primaryFormatId, templateId, content) => {
    const creative: Creative = {
      id: uuid(),
      campaignId,
      name,
      primaryFormatId,
      templateId,
      content,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await db.creatives.add(creative);

    // Update campaign's creativeIds
    const campaign = get().campaigns.find((c) => c.id === campaignId);
    if (campaign) {
      await db.campaigns.update(campaignId, {
        creativeIds: [...campaign.creativeIds, creative.id],
        updatedAt: new Date().toISOString(),
      });
    }

    set((s) => ({
      creatives: [...s.creatives, creative],
      campaigns: s.campaigns.map((c) =>
        c.id === campaignId
          ? { ...c, creativeIds: [...c.creativeIds, creative.id] }
          : c
      ),
    }));
    return creative;
  },

  updateCreative: async (id, updates) => {
    const updatedData = { ...updates, updatedAt: new Date().toISOString() };
    await db.creatives.update(id, updatedData);
    set((s) => ({
      creatives: s.creatives.map((c) =>
        c.id === id ? { ...c, ...updatedData } : c
      ),
    }));
  },

  deleteCreative: async (id) => {
    const creative = get().creatives.find((c) => c.id === id);
    if (creative) {
      await db.creatives.delete(id);
      // Update campaign's creativeIds
      const campaign = get().campaigns.find((c) => c.id === creative.campaignId);
      if (campaign) {
        await db.campaigns.update(creative.campaignId, {
          creativeIds: campaign.creativeIds.filter((cid) => cid !== id),
          updatedAt: new Date().toISOString(),
        });
      }
    }
    set((s) => ({
      creatives: s.creatives.filter((c) => c.id !== id),
      campaigns: s.campaigns.map((c) => ({
        ...c,
        creativeIds: c.creativeIds.filter((cid) => cid !== id),
      })),
      currentCreativeId: s.currentCreativeId === id ? null : s.currentCreativeId,
    }));
  },

  selectCreative: (id) => {
    if (id) {
      localStorage.setItem(LAST_CREATIVE_KEY, id);
    } else {
      localStorage.removeItem(LAST_CREATIVE_KEY);
    }
    set({ currentCreativeId: id });
  },

  duplicateCreative: async (id) => {
    const original = get().creatives.find((c) => c.id === id);
    if (!original) return null;

    const duplicate: Creative = {
      ...original,
      id: uuid(),
      name: `${original.name} (copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await db.creatives.add(duplicate);

    set((s) => ({ creatives: [...s.creatives, duplicate] }));
    return duplicate;
  },

  uploadAsset: async (file, campaignId) => {
    const asset: Asset = {
      id: uuid(),
      campaignId,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : 'design',
      mimeType: file.type,
      size: file.size,
      blob: file,
      createdAt: new Date().toISOString(),
    };
    await db.assets.add(asset);
    set((s) => ({ assets: [...s.assets, asset] }));
    return asset;
  },

  deleteAsset: async (id) => {
    await db.assets.delete(id);
    set((s) => ({ assets: s.assets.filter((a) => a.id !== id) }));
  },

  exportCreative: async (id) => {
    const creative = get().creatives.find((c) => c.id === id);
    if (!creative) throw new Error('Creative not found');
    const json = JSON.stringify(creative, null, 2);
    return new Blob([json], { type: 'application/json' });
  },

  importCreative: async (file, campaignId) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text) as Partial<Creative>;
      // Support both old (formatId) and new (primaryFormatId) formats
      const primaryFormatId = data.primaryFormatId || (data as any).formatId;
      const templateId = data.templateId || (data as any).stylePreset;

      if (!data.name || !primaryFormatId || !data.content) {
        throw new Error('Invalid creative file');
      }
      return get().createCreative(
        campaignId,
        data.name,
        primaryFormatId,
        templateId || 'eclipse',
        data.content
      );
    } catch {
      set({ error: 'Failed to import creative' });
      return null;
    }
  },
}));

// Legacy alias for migration compatibility
/** @deprecated Use useCreativeStore instead */
export const useProjectStore = useCreativeStore;

// Initialize on import
if (typeof window !== 'undefined') {
  useCreativeStore.getState().loadAll();
}
